import { useEffect, useRef, useState } from "react";
import { REGION_NAMES, comunasOf } from "../lib/chile";
import { geocode, geocodeStructured } from "../lib/geo";
import { PointMap } from "./RouteMap";
import { MapPin, CircleNotch, Check } from "@phosphor-icons/react";

/**
 * Selector Región -> Comuna + dirección con autocompletado (Nominatim).
 * value: { region, comuna, address, reference, lat, lng }
 */
export function LocationPicker({ value, onChange, accent = "#FF5A36", testid = "loc" }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  const set = (patch) => onChange({ ...value, ...patch });

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const onAddressChange = (text) => {
    set({ address: text });
    if (timer.current) clearTimeout(timer.current);
    if (!text || text.trim().length < 3) { setSuggestions([]); return; }
    setLoading(true);
    setOpen(true);
    timer.current = setTimeout(async () => {
      const res = await geocodeStructured(text, value.comuna, value.region);
      setSuggestions(res);
      setLoading(false);
    }, 550);
  };

  const pick = (s) => {
    // Preserva el número que el usuario escribió si la sugerencia no lo incluye.
    const typedNum = (value.address || "").match(/\b(\d{1,6})\b/);
    let label = s.label;
    if (typedNum && !/\d/.test(label)) label = `${label} ${typedNum[1]}`;
    set({ address: label, lat: s.lat, lng: s.lng });
    setOpen(false);
    setSuggestions([]);
  };

  // Al elegir comuna, geocodifica su centro como coordenada base (fallback).
  const onComunaChange = async (comuna) => {
    set({ comuna, lat: null, lng: null });
    const res = await geocode(`${comuna}, ${value.region}, Chile`);
    if (res[0]) onChange({ ...value, comuna, lat: res[0].lat, lng: res[0].lng });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Región</label>
        <select data-testid={`${testid}-region`} value={value.region || ""} onChange={(e) => onChange({ ...value, region: e.target.value, comuna: "", lat: null, lng: null })}
          className="mt-1.5 w-full h-12 px-4 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36]">
          <option value="">Selecciona una región</option>
          {REGION_NAMES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Comuna / Ciudad</label>
        <select data-testid={`${testid}-comuna`} value={value.comuna || ""} disabled={!value.region} onChange={(e) => onComunaChange(e.target.value)}
          className="mt-1.5 w-full h-12 px-4 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36] disabled:opacity-50">
          <option value="">{value.region ? "Selecciona una comuna" : "Primero elige la región"}</option>
          {comunasOf(value.region).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="relative" ref={boxRef}>
        <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Dirección</label>
        <div className="relative mt-1.5">
          <MapPin size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input data-testid={`${testid}-address`} value={value.address || ""} disabled={!value.comuna}
            onChange={(e) => onAddressChange(e.target.value)} onFocus={() => suggestions.length && setOpen(true)}
            placeholder={value.comuna ? "Calle y número" : "Primero elige la comuna"}
            className="w-full h-12 pl-10 pr-10 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36] disabled:opacity-50" />
          {loading && <CircleNotch size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 animate-spin text-[#FF5A36]" />}
          {!loading && value.lat != null && <Check size={18} weight="bold" className="absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-500" />}
        </div>
        {open && suggestions.length > 0 && (
          <div className="absolute z-30 mt-1 w-full bg-white rounded-xl border border-zinc-200 shadow-xl overflow-hidden" data-testid={`${testid}-suggestions`}>
            {suggestions.map((s, i) => (
              <button key={i} type="button" onClick={() => pick(s)} data-testid={`${testid}-suggestion-${i}`}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-zinc-50 flex items-start gap-2 border-b border-zinc-50 last:border-0">
                <MapPin size={16} className="text-[#FF5A36] mt-0.5 shrink-0" />
                <span>{s.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Referencia (opcional)</label>
        <input data-testid={`${testid}-reference`} value={value.reference || ""} onChange={(e) => set({ reference: e.target.value })}
          placeholder="Ej: portón azul, depto 402"
          className="mt-1.5 w-full h-12 px-4 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36]" />
      </div>

      {value.lat != null && (
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Ubicación en el mapa</label>
          <div className="mt-1.5"><PointMap lat={value.lat} lng={value.lng} /></div>
          <p className="text-xs text-zinc-400 mt-1.5">Verifica que el marcador esté en la dirección correcta.</p>
        </div>
      )}
    </div>
  );
}
